import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'

import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs'
import { logs, SeverityNumber } from '@opentelemetry/api-logs'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'

let appLogger = null

export function initOtel (appConfig) {
  if (!appConfig || appConfig.otelEnabled !== 'true') {
    return
  }

  try {
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: appConfig.otelServiceName,
      // Raw attribute name (not the newer semconv `deployment.environment.name`) so this
      // matches the key the .NET backend already emits from OTEL_RESOURCE_ATTRIBUTES,
      // keeping frontend and backend telemetry grouped under the same Elastic service view.
      'deployment.environment': appConfig.otelDeploymentEnvironment
    })

    const tracerProvider = new WebTracerProvider({
      resource,
      spanProcessors: [
        new BatchSpanProcessor(new OTLPTraceExporter({ url: appConfig.otelTracesEndpoint }))
      ]
    })
    tracerProvider.register({ contextManager: new ZoneContextManager() })

    registerInstrumentations({
      instrumentations: [
        new DocumentLoadInstrumentation(),
        // Same-origin only: no cross-origin calls exist today, so don't inject
        // traceparent headers anywhere that could trigger a CORS preflight.
        new FetchInstrumentation({ propagateTraceHeaderCorsUrls: [] }),
        new XMLHttpRequestInstrumentation({ propagateTraceHeaderCorsUrls: [] })
      ]
    })

    const loggerProvider = new LoggerProvider({
      resource,
      processors: [new BatchLogRecordProcessor(new OTLPLogExporter({ url: appConfig.otelLogsEndpoint }))]
    })
    logs.setGlobalLoggerProvider(loggerProvider)
    appLogger = logs.getLogger(appConfig.otelServiceName)

    installErrorHandlers()
  } catch (e) {
    // Telemetry init must never break app bootstrap.
    // eslint-disable-next-line no-console
    console.error('OTel init failed', e)
  }
}

export function logError (error, extra = {}) {
  if (!appLogger) return
  appLogger.emit({
    severityNumber: SeverityNumber.ERROR,
    severityText: 'ERROR',
    body: (error && error.message) || String(error),
    attributes: {
      'exception.stacktrace': error && error.stack,
      'exception.type': error && error.name,
      ...extra
    }
  })
}

function installErrorHandlers () {
  window.addEventListener('error', (event) => {
    logError(event.error || new Error(event.message), { source: 'window.onerror' })
  })
  window.addEventListener('unhandledrejection', (event) => {
    logError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)), { source: 'unhandledrejection' })
  })
}
